import api from './api';
import { FormReviewData } from '@/types';
import { apiCache, cacheTTL } from '@/utils/api-cache';

export interface ApproveFormResponse {
  message: string;
  travelerId: string;
  status: string;
}

export interface FlagFormRequest {
  reason: string;
}

export interface FlagFormResponse {
  message: string;
  travelerId: string;
  status: string;
}

export interface AddNoteRequest {
  content: string;
  type: 'pharmacy_note' | 'internal_note';
}

export interface AddNoteResponse {
  message: string;
  noteId: string;
}

// Administration site options for injections
export type AdministrationSite =
  | 'Left Subcutaneous'
  | 'Right Subcutaneous'
  | 'Left Deltoid Intramuscular'
  | 'Right Deltoid Intramuscular';

export const ADMINISTRATION_SITES: AdministrationSite[] = [
  'Left Subcutaneous',
  'Right Subcutaneous',
  'Left Deltoid Intramuscular',
  'Right Deltoid Intramuscular',
];

export interface RecommendationSelection {
  item_type: 'vaccine' | 'medication';
  item_key: string;
  action: 'select' | 'deselect';
  note?: string;
  source?: 'pharmacist_added' | 'not_recommended';
  refill_info?: string;
  administration_site?: AdministrationSite;
  quantity?: string;
}

export interface SelectRecommendationsRequest {
  recommendation_id: string;
  selections: RecommendationSelection[];
  pharmacist_note?: string;
}

export interface SelectRecommendationsResponse {
  message: string;
  recommendation_id: string;
  updated_selections: RecommendationSelection[];
}

export interface ManualSelectionsRequest {
  selections: RecommendationSelection[];
}

export interface ManualSelectionsResponse {
  message: string;
  traveler_id: string;
  results: Array<{
    item_type: string;
    item_key: string;
    action: string;
  }>;
}

export const travelerService = {
  /**
   * Get traveler form details with caching
   */
  async getTravelerForm(travelerId: string): Promise<FormReviewData> {
    const cacheKey = `traveler:form:${travelerId}`;
    
    return apiCache.fetch(
      cacheKey,
      async () => {
        const response = await api.get<FormReviewData>(
          `/pharmacy/travelers/${travelerId}/form`
        );
        
        return response.data;
      },
      cacheTTL.short // 1 minute - frequently changing data
    );
  },

  /**
   * Start reviewing a form (changes status to "under_review")
   * Invalidates traveler form cache and dashboard cache
   */
  async startReview(travelerId: string): Promise<void> {
    await api.post(`/pharmacy/travelers/${travelerId}/review`);
    
    // Invalidate related caches
    apiCache.invalidate(`traveler:form:${travelerId}`);
    apiCache.invalidatePattern(/^dashboard/);
    apiCache.invalidatePattern(/^forms:list/);
  },

  /**
   * Approve a form (changes status to "completed")
   * Invalidates traveler form cache and dashboard cache
   */
  async approveForm(travelerId: string): Promise<ApproveFormResponse> {
    const response = await api.post<ApproveFormResponse>(
      `/pharmacy/travelers/${travelerId}/approve`
    );
    
    // Invalidate related caches
    apiCache.invalidate(`traveler:form:${travelerId}`);
    apiCache.invalidatePattern(/^dashboard/);
    apiCache.invalidatePattern(/^forms:list/);
    
    return response.data;
  },

  /**
   * Flag a form for review
   * Invalidates traveler form cache and dashboard cache
   */
  async flagForm(travelerId: string, reason: string): Promise<FlagFormResponse> {
    const response = await api.post<FlagFormResponse>(
      `/pharmacy/travelers/${travelerId}/flag`,
      { reason }
    );
    
    // Invalidate related caches
    apiCache.invalidate(`traveler:form:${travelerId}`);
    apiCache.invalidatePattern(/^dashboard/);
    apiCache.invalidatePattern(/^forms:list/);
    
    return response.data;
  },

  /**
   * Add a note to a traveler form
   * Invalidates traveler form cache (note added to form)
   */
  async addNote(
    travelerId: string,
    content: string,
    type: 'pharmacy_note' | 'internal_note' = 'pharmacy_note'
  ): Promise<AddNoteResponse> {
    const response = await api.post<AddNoteResponse>(
      `/pharmacy/travelers/${travelerId}/notes`,
      { content, type }
    );
    
    // Invalidate traveler form cache (notes are part of the form)
    apiCache.invalidate(`traveler:form:${travelerId}`);
    
    return response.data;
  },

  /**
   * Select/deselect recommendations for a traveler
   * Invalidates traveler form cache
   */
  async selectRecommendations(
    travelerId: string,
    data: SelectRecommendationsRequest
  ): Promise<SelectRecommendationsResponse> {
    const response = await api.post<SelectRecommendationsResponse>(
      `/pharmacy/travelers/${travelerId}/recommendations/select`,
      data
    );
    
    // Invalidate traveler form cache (recommendations are part of the form)
    apiCache.invalidate(`traveler:form:${travelerId}`);
    
    return response.data;
  },

  /**
   * Add manual pharmacist selections (without recommendation_id)
   * Used for items not in AI recommendations
   * Invalidates traveler form cache
   */
  async addManualSelections(
    travelerId: string,
    data: ManualSelectionsRequest
  ): Promise<ManualSelectionsResponse> {
    const response = await api.post<ManualSelectionsResponse>(
      `/pharmacy/travelers/${travelerId}/recommendations/select`,
      data
    );
    
    // Invalidate traveler form cache
    apiCache.invalidate(`traveler:form:${travelerId}`);
    
    return response.data;
  },
};
